import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ModalService } from 'service/modal.service';
import { TechniqueSelectionComponent } from 'component/technique-selection/technique-selection.component';
import { PeerCursor } from '@udonarium/peer-cursor';
import { Network, EventSystem } from '@udonarium/core/system';
import { PeerContext } from '@udonarium/core/system/network/peer-context';
import { TabletopActionService } from 'service/tabletop-action.service';
import { ObjectStore } from '@udonarium/core/synchronize-object/object-store';
import { FileArchiver } from '@udonarium/core/file-storage/file-archiver';

interface Ougi {
    id: number;
    name: string;
    text: string;
}

interface Player {
    name: string;
    available: { [key: string]: boolean | null };
}

interface Room {
    roomName: string;
    roomId?: string;
    player1: string;
    player2: string;
    url?: string;
    winner?: string | null;
    // isBO3?: boolean;
    // scores?: number[];
    // gameWinners?: (string | null)[];
}

interface Round {
    roundName: string;
    rooms: Room[];
}

interface TournamentData {
    season: string;
    description: string;
    Ougis?: Ougi[];
    players?: Player[];
    rounds: Round[];
}

@Component({
    selector: 'tournament-board',
    templateUrl: './tournament-board.component.html',
    styleUrls: ['./tournament-board.component.css']
})
export class TournamentBoardComponent implements OnInit {
    tournaments: TournamentData[] = [];
    selectedTournament: TournamentData | null = null;
    selectedRound: Round | null = null;
    showRanking: boolean = false;

    // ご提示いただいたAPIの絶対パスを使用
    private readonly API_URL = 'https://mitarashi.link/events_api/events-api.php';

    constructor(
        private http: HttpClient,
        private modalService: ModalService,
        private tabletopActionService: TabletopActionService,
        private changeDetector: ChangeDetectorRef
    ) { }

    get rankings(): any[] {
        if (!this.selectedTournament || !this.selectedTournament.players) return [];

        const stats: { [key: string]: any } = {};
        this.selectedTournament.players.forEach(p => {
            stats[p.name] = {
                name: p.name, wins: 0, matches: 0,
                opponents: [], gw: 0, ogw: 0
            };
        });

        this.selectedTournament.rounds.forEach(r => {
            r.rooms.forEach(room => {
                if (room.winner) {
                    if (stats[room.player1]) {
                        stats[room.player1].matches++;
                        stats[room.player1].opponents.push(room.player2);
                        if (room.winner === room.player1) stats[room.player1].wins++;
                    }
                    if (room.player2 !== '(Bye)' && stats[room.player2]) {
                        stats[room.player2].matches++;
                        stats[room.player2].opponents.push(room.player1);
                        if (room.winner === room.player2) stats[room.player2].wins++;
                    }
                }
            });
        });

        const playerArray = Object.values(stats);
        playerArray.forEach(p => {
            p.gw = p.matches > 0 ? p.wins / p.matches : 0;
        });

        playerArray.forEach(p => {
            const totalOpponentGW = p.opponents.reduce((acc: number, oppName: string) => {
                const opp = stats[oppName];
                return acc + (opp ? Math.max(0.33, opp.gw) : 0.33);
            }, 0);
            p.ogw = p.opponents.length > 0 ? totalOpponentGW / p.opponents.length : 0;
        });

        return playerArray.sort((a, b) => b.wins - a.wins || b.gw - a.gw || b.ogw - a.ogw);
    }

    ngOnInit(): void {
        // NG0100エラー(ExpressionChangedAfterItHasBeenCheckedError)対策
        setTimeout(() => {
            if (this.modalService.option) {
                this.modalService.title = this.modalService.option.title || '大会ボード';
            }
            this.loadTournaments(['2026_spring.json']);
            this.changeDetector.detectChanges();
        });
    }

    loadTournaments(files: string[]) {
        files.forEach(file => {
            const apiUrl = `${this.API_URL}?file=${file}`;
            this.http.get<TournamentData>(apiUrl).subscribe(data => {
                if (data && data.rounds) {
                    if (!this.tournaments.find(t => t.season === data.season)) {
                        (data as any).filename = file;
                        this.tournaments.push(data);
                        if (!this.selectedTournament) {
                            this.selectTournament(data);
                        }
                    }
                }
                this.changeDetector.detectChanges();
            }, error => {
                console.warn(`Failed to fetch ${file} from API:`, error);
            });
        });
    }

    get displayRounds(): any[] {
        if (!this.selectedTournament || !this.selectedTournament.rounds) return [];
        const list: any[] = [];
        let finalsAdded = false;

        for (const r of this.selectedTournament.rounds) {
            if (r && r.roundName && r.roundName.includes('決勝')) {
                if (!finalsAdded) {
                    list.push({ roundName: '決勝トーナメント', isFinalsGroup: true });
                    finalsAdded = true;
                }
            } else if (r) {
                list.push(r);
            }
        }
        return list;
    }

    selectTournament(tournament: TournamentData) {
        if (!tournament) return;
        this.selectedTournament = tournament;
        this.selectedRound = null;
        if (tournament.rounds && tournament.rounds.length > 0) {
            this.selectRound(tournament.rounds[0]);
        }
    }

    selectRound(round: any) {
        if (!round) return;
        this.showRanking = false;
        if (round.isFinalsGroup) {
            const finals = this.selectedTournament?.rounds?.filter(r => r.roundName && r.roundName.includes('決勝'));
            this.selectedRound = (finals && finals.length > 0) ? finals[finals.length - 1] : null;
        } else {
            this.selectedRound = round;
        }
        this.changeDetector.detectChanges();
    }

    toggleRanking() {
        this.showRanking = true;
        this.selectedRound = null;
        this.changeDetector.detectChanges();
    }

    async joinRoom(room: Room, playerName: string) {
        if (PeerCursor.myCursor) {
            PeerCursor.myCursor.name = playerName;
        }

        const player = this.selectedTournament?.players?.find(p => p.name === playerName);
        if (!player) {
            if (room.url) window.open(room.url, '_blank');
            return;
        }

        const selected = await this.modalService.open<string[]>(TechniqueSelectionComponent, {
            player: player,
            ougis: this.selectedTournament?.Ougis || [],
            wide: true
        });

        if (selected) {
            if (PeerCursor.myCursor) {
                PeerCursor.myCursor.technique = selected;
            }

            /*
            selected.forEach(tech => {
                if (player.available[tech] !== undefined) {
                    player.available[tech] = null;
                }
            });

            this.saveTournamentData();
            */

            const targetRoomId = room.roomId || 'S999';
            const displayRoomName = `【新春オンライン大会】${room.roomName}`;
            const password = '';
            const capacity = '3';

            const peerIds = await Network.listAllPeers();
            const existingContexts: PeerContext[] = [];

            for (let peerId of peerIds) {
                let context = PeerContext.parse(peerId);
                if (context.isRoom && context.roomId === targetRoomId) {
                    existingContexts.push(context);
                }
            }

            if (existingContexts.length > 0) {
                await this.tabletopActionService.deleteAllCards(true);
                await this.tabletopActionService.deleteAllCardStacks();
                await this.tabletopActionService.deleteAllCharacters();
                await this.tabletopActionService.deleteAllDiceSymbols(true);
                await this.tabletopActionService.deleteAlltableMasks();
                await this.tabletopActionService.deleteAllTerrains();

                let userId = Network.peerContext ? Network.peerContext.userId : PeerContext.generateId();
                // @ts-ignore
                Network.open(userId, targetRoomId, displayRoomName, password, '3'); // capacity must be string

                if (PeerCursor.myCursor) {
                    PeerCursor.myCursor.peerId = Network.peerId;
                }

                const triedPeer: string[] = [];
                const handler = EventSystem.register(triedPeer).on('OPEN_NETWORK', event => {
                    EventSystem.unregister(handler);
                    ObjectStore.instance.clearDeleteHistory();
                    for (let context of existingContexts) {
                        Network.connect(context.peerId);
                    }
                });
            } else {
                const userId = Network.peerContext ? Network.peerContext.userId : PeerContext.generateId();
                Network.open(userId, targetRoomId, displayRoomName, password, '3');

                if (PeerCursor.myCursor) {
                    PeerCursor.myCursor.peerId = Network.peerId;
                }
            }

            this.modalService.resolve();
        }
    }

    saveTournamentData() {
        if (!this.selectedTournament) return;
        const filename = (this.selectedTournament as any).filename;
        if (!filename) return;

        this.http.post(`${this.API_URL}?file=${filename}`, this.selectedTournament).subscribe(
            res => console.log('Tournament data updated successfully'),
            err => console.error('Failed to update tournament data', err)
        );
    }

    get isFinalsRound(): boolean {
        return !!(this.selectedRound && this.selectedRound.roundName && this.selectedRound.roundName.includes('決勝'));
    }

    get finalsRounds(): Round[] {
        if (!this.selectedTournament || !this.selectedTournament.rounds) return [];
        return this.selectedTournament.rounds.filter(r => r.roundName && r.roundName.includes('決勝'));
    }

    getPairs(rooms: Room[]): Room[][] {
        const pairs: Room[][] = [];
        for (let i = 0; i < rooms.length; i += 2) {
            pairs.push(rooms.slice(i, i + 2));
        }
        return pairs;
    }

    async loadLog(roomId: string) {
        try {
            const url = `${this.API_URL}?action=downloadLog&roomId=${roomId}`;
            const blob = await this.http.get(url, { responseType: 'blob' }).toPromise();
            if (blob) {
                const file = new File([blob], `${roomId}.zip`, { type: 'application/zip' });
                await FileArchiver.instance.load([file]);
                this.modalService.resolve();
            }
        } catch (error) {
            console.error('Failed to load log zip', error);
            const status = error.status ? ` (Status: ${error.status})` : '';
            const url = `${this.API_URL}?action=downloadLog&roomId=${roomId}`;
            alert('ログの読み込みに失敗しました。' + status + '\nURL: ' + url);
        }
    }
    close() {
        this.modalService.resolve();
    }
}
